import React, { useEffect, useRef, useState } from "react";
import { ContactCentreAddNewProps } from "./contact-centre-add-new.props";
import { useAuth } from "../auth/auth-context";
import { NewContactCentreViewModel } from "./contact-centre-add-new.view-model";
import {
	IContactCentreModel,
	TContactCenterIntegrationOption,
} from "src/types/models/apiModels";
import InputArray from "src/components/molecules/input-array/input-array";
import Dropdown from "src/components/atoms/dropdown/dropdown";
import ActionPanel from "../action-panel/action-panel";
import LoadingModal from "src/components/molecules/loading/LoadingModal";
import RadioGroup from "src/components/molecules/radio-button-group/radio-button-groups";
import HyperLink from "src/components/atoms/hyper-link/hyper-link";
import { ChevronRight } from "@mui/icons-material";
import CopyToClipboardButton from "src/components/atoms/copy-to-clipboard-button/copy-to-clipboard-button";
import InputBox from "src/components/atoms/v2/inputBox/inputBox";
import Modal from "src/components/molecules/modal/modal";
import Alert from "src/components/atoms/alert/alert";
import { HelpCircleIcon } from "lucide-react";

const ContactCentreAddNew: React.FC<ContactCentreAddNewProps> = ({
	contactCentreInput,
	onSave,
	onCancel,
}) => {
	const { user } = useAuth(); // Extract user from useAuth hook
	const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false);
	const [error, setError] = useState<string>("");
	const [loading, setLoading] = useState(false);
	const [contactCenterIntegrationOption, setContactCenterIntegrationOption] =
		useState<TContactCenterIntegrationOption>("WEBSOCKET");
	const [webHookUrl, setWebHookUrl] = useState<string>("");
	const [contactCentre, setContactCentre] = useState<IContactCentreModel>(
		contactCentreInput ?? {
			businessId: "",
			name: "",
			status: "New",
			allowedUrls: [],
			contactCentreCode: "",
			contactCentreId: "",
			option: "ExternalContactCentre",
			productName: "Custom",
			eventDeliveryMethod: "WEBSOCKET",
			enabledEvents: [],
		}
	);
	const viewModel = useRef<NewContactCentreViewModel | null>(null);

	useEffect(() => {
		if (user?.accessToken) {
			viewModel.current = new NewContactCentreViewModel(user.accessToken);
		}

		if (contactCentreInput) {
			setContactCentre(contactCentreInput);
			setWebHookUrl(contactCentreInput.webhookConfig?.webhookUrl ?? "");
			if (contactCentreInput.eventDeliveryMethod) {
				setContactCenterIntegrationOption(
					contactCentreInput.eventDeliveryMethod
				);

				if (contactCentreInput.eventDeliveryMethod === "WEBHOOK") {
					setWebHookUrl(
						contactCentreInput.webhookConfig?.webhookUrl ?? ""
					);
				} else if (
					contactCentreInput.eventDeliveryMethod === "WEBSOCKET"
				) {
					setWebHookUrl("");
				}
			}
		}
	}, []);

	const handleFieldChange = (
		field: keyof typeof contactCentre,
		value: string | string[]
	) => {
		setContactCentre((prevInfo) => ({
			...prevInfo,
			[field]: value,
		}));
	};

	const handleCancel = () => {
		if (onCancel) {
			onCancel();
		}
	};

	const toggleError = (value: boolean) => {
		console.log("In toggleError");
		setErrorDialogOpen((prevValue) => {
			if (prevValue !== value) {
				return value;
			}
			return prevValue;
		});
	};

	const closeError = () => {
		console.log("In closeError");
		toggleError(false);
	};

	const handleOnChange = (inputStrings: string[]) => {
		handleFieldChange("allowedUrls", inputStrings);
	};

	const handleContactCenterIntegrationOptionChange = (selectedOption: {
		label: string;
		value: string;
	}) => {
		if (selectedOption.value === "WEBHOOK") {
			setContactCenterIntegrationOption("WEBHOOK");
		} else if (selectedOption.value === "WEBSOCKET") {
			setContactCenterIntegrationOption("WEBSOCKET");
		}
	};

	const handleSubmit = async (): Promise<void> => {
		if (contactCentre) {
			if (contactCentre.contactCentreId) {
				// this is a update scenario
				console.log(
					`Contact centre to be updated... ${contactCentre.contactCentreId}`
				);
			} else {
				contactCentre.eventDeliveryMethod =
					contactCenterIntegrationOption;
				if (contactCentre.eventDeliveryMethod === "WEBHOOK") {
					contactCentre.webhookConfig = {
						webhookUrl: webHookUrl,
						authMethod: "JWT",
						httpMethod: "POST",
						retryPolicy: {
							maxRetries: 3,
							retryIntervalInSeconds: 10,
						},
					};
				} else if (contactCentre.eventDeliveryMethod === "WEBSOCKET") {
					contactCentre.webhookConfig = undefined;
				}
				const validate =
					await viewModel.current?.validateContactCentre(
						contactCentre
					);

				if (validate && validate.length > 0) {
					setError(() => validate.join(","));
					toggleError(true);
				} else {
					setLoading(true);
					contactCentre.option = "ExternalContactCentre";
					const response =
						await viewModel.current?.saveContactCentre(
							contactCentre
						);

					if (response?.result === true) {
						if (onSave) {
							setLoading(false);
							onSave(contactCentre);
						}
					} else {
						setLoading(false);
						setError(() => response?.error ?? "");
						toggleError(true);
					}
				}
			}
		}
	};

	return (
		<div className="bg-white p-6 flex flex-col items-center justify-center gap-4 w-full">
			<div className="flex flex-col items-center justify-start gap-4 w-full">
				<InputBox
					label={"Name"}
					type={"text"}
					value={contactCentre?.name ?? ""}
					onChange={(value) => handleFieldChange("name", value)}
					required
				></InputBox>

				<Dropdown
					label="Contact centre provider"
					value={contactCentre?.productName ?? "AU"}
					onChange={(value) =>
						handleFieldChange("productName", value)
					}
					menuItems={[
						{ value: "Custom", label: "Custom" },
						{ value: "Avaya", label: "Avaya" },
						{ value: "Cisco-UCCE", label: "Cisco-UCCE" },
						{ value: "Cisco-Webex", label: "Cisco-Webex" },
						{ value: "Genesys Cloud", label: "Genesys Cloud" },
						{
							value: "Genesys Engage",
							label: "Genesys Engage",
						},
						{
							value: "Genesys PureConnect",
							label: "Genesys PureConnect",
						},
						{ value: "NICE-CXone", label: "NICE-CXone" },
						{ value: "Five9", label: "Five9" },
						{
							value: "Microsoft Dynamics 365",
							label: "Microsoft Dynamics 365",
						},
						{
							value: "Azure Communication Services",
							label: "Azure Communication Services",
						},
						{
							value: "Amazon Connect",
							label: "Amazon Connect",
						},
						{ value: "RingCentral", label: "RingCentral" },
						{
							value: "Mitel-MiContact",
							label: "Mitel-MiContact",
						},
						{ value: "Twilio Flex", label: "Twilio Flex" },
						{ value: "Vonage", label: "Vonage" },
						{ value: "SAP", label: "SAP" },
						{ value: "Talkdesk", label: "Talkdesk" },
						{ value: "8x8", label: "8x8" },
						{ value: "Zendesk Talk", label: "Zendesk Talk" },
						{ value: "Zendesk Suite", label: "Zendesk Suite" },
					]}
				/>
				<div className="w-full flex flex-col items-start justify-center gap-4">
					<div className="flex flex-col items-start justify-center gap-4 w-full">
						<span className="font-Poppins font-medium text-xl">
							Secured calls integration option
						</span>
						<RadioGroup
							options={[
								{
									label: `SDK`,
									value: "WEBSOCKET",
								},
								{
									label: "Webhook",
									value: "WEBHOOK",
								},
							]}
							defaultSelected={contactCenterIntegrationOption}
							onChange={
								handleContactCenterIntegrationOptionChange
							}
						/>
						{contactCenterIntegrationOption === "WEBSOCKET" ? (
							<>
								<div className="font-Poppins font-semibold flex items-center justify-center gap-1 p-2">
									<span className="font-Poppins font-semibold text-md tracking-wider">
										Install the SDK using the command
										below,and refer
									</span>
									<HyperLink
										href={
											"https://docs.development.securedcalls.com/web_sdk"
										}
										label={"documentation"}
										target="_blank"
									></HyperLink>
									<span> for implementation help</span>
									<HelpCircleIcon className="w-6 h-6"></HelpCircleIcon>
								</div>

								<div className="flex items-center justify-center gap-2 border-2 border-gray-300 p-2">
									<span className="font-Poppins font-medium text-sm tracking-wider ">
										<ChevronRight className="text-gray-700"></ChevronRight>{" "}
										npm i @expertstack-studios/sc-web-sdk-v2
									</span>
									<CopyToClipboardButton
										text={
											"npm i @expertstack-studios/sc-web-sdk-v2"
										}
									></CopyToClipboardButton>
								</div>
							</>
						) : (
							<div className="flex flex-col items-start justify-center gap-4 w-full">
								<div className="flex items-center justify-center w-full border gap-2">
									<span className="font-Poppins font-semibold bg-gray-300 w-fit h-fit py-2 text-nowrap px-2">
										HTTP POST
									</span>
									{/* <input className="font-Poppins font-normal text-sm w-full py-2 px-2 focus:border-none"></input> */}
									<input
										value={webHookUrl}
										onChange={(e) =>
											setWebHookUrl(e.target.value)
										}
										required={
											contactCenterIntegrationOption ===
											"WEBHOOK"
										}
										className="font-Poppins font-normal text-sm w-full py-2 px-2 focus:outline-none focus:ring-0"
									/>
								</div>
								<Alert
									text={
										"Signature secret to verify the request will be sent on email when this Contact center is used in a branding application"
									}
									type={"info"}
								></Alert>
							</div>
						)}
					</div>
					<span className="font-Poppins font-medium text-xl">
						Allowed URLs (3 URls max)
					</span>
					<InputArray
						inputString={contactCentreInput?.allowedUrls}
						label="Allowed URLs"
						onChange={handleOnChange}
					/>
				</div>
			</div>
			<ActionPanel
				primaryButtonLabel={"Submit"}
				primaryButtonEnabled={
					contactCentre &&
					contactCentre.name != "" &&
					contactCentre.allowedUrls.length > 0
				}
				secondaryButtonLabel={"Cancel"}
				secondaryButtonEnabled={true}
				primaryButtonAction={handleSubmit}
				secondaryButtonAction={handleCancel}
			></ActionPanel>
			<Modal isOpen={errorDialogOpen} onClose={closeError}>
				{error.split(",").map((err, index) => (
					<Alert key={index} type="error" text={err.trim()} />
				))}
			</Modal>
			<LoadingModal
				message="Saving Contact Center"
				open={loading}
			></LoadingModal>
		</div>
	);
};

export default ContactCentreAddNew;
